import { Injectable } from '@angular/core';
import { EchoService } from './echo.service';
import { Subject, Observable } from 'rxjs';
import { AuthService } from '../modules/auth/auth.service';

export interface PermissionChangeEvent {
	userId: number;
	userName: string;
	addedPermissions: string[];
	removedPermissions: string[];
	updatedBy: {
		id: number;
		name: string;
	};
	timestamp: string;
	message: string;
}

@Injectable({
	providedIn: 'root'
})
export class EchoPermissionChangeService {
	private permissionChangeSubject = new Subject<PermissionChangeEvent>();
	public permissionChange$ = this.permissionChangeSubject.asObservable();

	constructor(
		private echoService: EchoService,
		private authService: AuthService
	) {}

	/**
	 * Listen to permission changes for a specific user
	 * @param userId The ID of the user to listen for permission changes
	 */
	listenToUserPermissionChanges(userId: number): void {
		// Subscribe to the private channel for user permissions
		const privateChannel = this.echoService.privateChannel(`user-permissions.${userId}`);

		if (privateChannel) {
			// Listen for the permissions.updated event
			privateChannel.listen('.permissions.updated', (data: any) => {
				// Emit the event for other components to listen to
				const permissionChangeEvent: PermissionChangeEvent = {
					userId: data.user_id,
					userName: data.user_name,
					addedPermissions: data.added_permissions || [],
					removedPermissions: data.removed_permissions || [],
					updatedBy: data.updated_by,
					timestamp: data.timestamp,
					message: data.message
				};

				const currentUserPermissions = this.authService.getPermissions();

				currentUserPermissions.forEach((permission: string) => {
					const index = currentUserPermissions.indexOf(permission);
					if (index > -1 && data.removed_permissions.includes(permission)) {
						currentUserPermissions.splice(index, 1);
					}
				});
				data.added_permissions.forEach((permission: string) => {
					if (!currentUserPermissions.includes(permission)) {
						currentUserPermissions.push(permission);
					}
				});

				this.authService.setPermissions(currentUserPermissions);
				// Permissions changed in real-time; drop the cached roles-permissions
				// request so the next fetch reflects the new permission set.
				this.authService.invalidateRolesPermissions();

				this.permissionChangeSubject.next(permissionChangeEvent);
			});
		} else {
			console.error(`❌ Failed to set up permission change listener for user ${userId} - channel not available`);
		}
	}

	/**
	 * Stop listening to permission changes (cleanup)
	 * @param userId The ID of the user to stop listening for
	 */
	stopListening(userId: number): void {
		// Note: You might want to implement channel cleanup logic here
		// depending on your Echo service implementation
	}
}
